import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure, useColorModeValue } from '@chakra-ui/react';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { useOrganizations, useCreateOrganization, useUpdateOrganization, useDeleteOrganization } from '@/lib/sdk/organization/queries';
import type { Organization } from '@/lib/sdk/organization/types';
import type { OrganizationFormData } from '@/modules/auth/components/OrganizationModal';
import { useLocalStorage } from '@/modules/workflows/hooks/useLocalStorage';
import { firstFeaturePath } from '@/modules/auth/featurePaths';

export function useOrgsPage() {
  const navigate = useNavigate();
  const { setCurrentOrg, user } = useAuth();
  const toast = useToast();
  const { data, isLoading, error } = useOrganizations({ offset: 0, limit: 1000 });

  const { getItem, setItem, removeItem } = useLocalStorage<string>('orgs-search', {
    persistAcrossSessions: true,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [deletingOrg, setDeletingOrg] = useState<Organization | null>(null);

  useEffect(() => {
    const savedSearch = getItem();
    if (savedSearch) setSearchQuery(savedSearch);
  }, [getItem]);

  useEffect(() => {
    if (searchQuery) {
      setItem(searchQuery);
    } else {
      removeItem();
    }
  }, [searchQuery, setItem, removeItem]);

  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const createMutation = useCreateOrganization();
  const updateMutation = useUpdateOrganization();
  const deleteMutation = useDeleteOrganization();

  const isSuperAdmin = user?.is_super_admin === true;

  const filteredOrganizations = useMemo(() => {
    if (!data?.items) return [];
    if (!searchQuery.trim()) return data.items;
    const query = searchQuery.toLowerCase();
    return data.items.filter(org =>
      org.name.toLowerCase().includes(query) ||
      org.email?.toLowerCase().includes(query) ||
      org.city?.toLowerCase().includes(query) ||
      org.country?.toLowerCase().includes(query)
    );
  }, [data?.items, searchQuery]);

  const handleSelectOrg = async (org: Organization) => {
    let isCurrent: boolean;
    try {
      isCurrent = await setCurrentOrg(org);
    } catch {
      toast.error(`Couldn't open ${org.name}. Please try again.`);
      return;
    }
    // A later click superseded this one; that click navigates.
    if (!isCurrent) return;
    const firstFeature = isSuperAdmin ? 'workflows' : (firstFeaturePath(org.feature_access || []) || 'reports');
    navigate(`/dashboard/${org.id}/${firstFeature}`);
  };

  const handleSuperAdminDashboard = async (e: React.MouseEvent, org: Organization) => {
    e.stopPropagation();
    if (await setCurrentOrg(org)) navigate(`/dashboard/${org.id}/workflows`);
  };

  const handleModalOpen = (action: string, org?: Organization) => {
    if (!isSuperAdmin) return;
    setEditingOrg(action === 'edit' && org ? org : null);
    onModalOpen();
  };

  const handleSubmit = async (formData: OrganizationFormData) => {
    if (!isSuperAdmin) return;
    try {
      formData.phone_number = formData.country_code + formData.phone_number;
      if (editingOrg) {
        await updateMutation.mutateAsync({ id: editingOrg.id, data: formData });
        toast.success('Organization updated successfully');
      } else {
        await createMutation.mutateAsync(formData);
        toast.success('Organization added successfully');
      }
      onModalClose();
      setEditingOrg(null);
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong');
      throw error;
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, org: Organization) => {
    if (!isSuperAdmin) return;
    e.stopPropagation();
    setDeletingOrg(org);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!isSuperAdmin || !deletingOrg) return;
    try {
      await deleteMutation.mutateAsync(deletingOrg.id);
      toast.success(`Organization "${deletingOrg.name}" deleted successfully`);
      onDeleteClose();
      setDeletingOrg(null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete organization');
    }
  };

  return {
    isSuperAdmin, data, isLoading, error,
    searchQuery, setSearchQuery,
    filteredOrganizations,
    editingOrg, deletingOrg,
    cardGradient,
    isModalOpen, onModalClose,
    isDeleteOpen, onDeleteClose,
    isPendingDelete: deleteMutation.isPending,
    handleSelectOrg, handleSuperAdminDashboard,
    handleModalOpen, handleSubmit, handleDeleteClick, handleDeleteConfirm,
  };
}
